import queryString from 'querystring';
import type * as H from 'history';
import { RANGE_OPTIONS, DEFAULT_VISIBLE_STORES } from './constants';
import type { CampaignPageQuery, CategoryTree } from 'src/types';
import type {
  CampaignFilterStore,
  RouterParams,
  SelectedFilter,
} from './types';
import type {
  ParamsAndReturnTypes,
  TranslateFunc,
} from 'src/components/translate';

export const buildRouter = (location: Omit<H.Location, 'state' | 'hash'>) => {
  const qs = queryString.decode(location.search.substr(1));

  const router = {
    qs,
    offset: qs.offset ? parseInt(qs.offset as string, 10) : 0,
    query: qs.query ? (qs.query as string) : '',
    sort: qs.sort as string,
    filterRanges: {},
    categories: qs.category
      ? ((qs.category as string).split('|') as string[])
      : [],
    stores: qs.stores ? (qs.stores as string).split('|') : [],
    maxStores: parseInt(qs.max_stores as string, 10) || DEFAULT_VISIBLE_STORES,
  };

  RANGE_OPTIONS.forEach(option => {
    router.filterRanges[option.key] = '';
    if (qs[option.key]) {
      const range = (qs[option.key] as string).split('-');
      if (range.length === 2) {
        router.filterRanges[option.key] = {
          from: Number(range[0]),
          to: Number(range[1]),
        };
      }
    }
  });

  return router;
};

const findTreeNode = (
  value: string,
  parents?: string[],
  tree?: ((CategoryTree & { parents?: string[] }) | null)[] | null
) => {
  if (!tree) return null;

  // Check all direct children
  let foundNode = tree.find((node?: CategoryTree | null) => node?.id === value);

  if (foundNode) {
    foundNode.parents = parents;
    return foundNode;
  }

  // Do recursive search if it wasn't found in the direct children
  tree.some(node => {
    if (node?.children && node?.children.length > 0) {
      // Add the current node id to the parents
      foundNode = findTreeNode(
        value,
        [...(parents ?? []), ...(node.id ? [node.id] : [])],
        node.children
      );

      if (foundNode) return true;
    }
    return false;
  });

  // Return result, will contain the node if found, otherwise false
  return foundNode;
};

export const getSelectedFilters = (
  router: RouterParams,
  stores: CampaignFilterStore[],
  categoryTree: CampaignPageQuery['categoryTree'],
  t: TranslateFunc
) => {
  const selectedFilters: SelectedFilter[] = [];

  // Add the filtered categories
  router.categories.forEach(id => {
    const node = findTreeNode(id, [], categoryTree);
    if (node?.id && node.name) {
      selectedFilters.push({
        id: node.id,
        name: node.name,
        type: 'category',
        isGrouped: true,
      });
    }
  });

  router.stores.forEach(id => {
    const store = stores.filter(x => x.id === id);
    if (store.length > 0) {
      selectedFilters.push({
        id: store[0].id,
        name: store[0].name,
        type: 'stores',
        isGrouped: true,
      });
    }
  });

  // Add the filter ranges
  RANGE_OPTIONS.forEach(option => {
    if (router.filterRanges[option.key]) {
      const name = `${t(
        'campaigns',
        option.translation as keyof ParamsAndReturnTypes['campaigns']
      )}: ${router.filterRanges[option.key].from} - ${
        router.filterRanges[option.key].to
      }`;

      selectedFilters.push({
        id: `${router.filterRanges[option.key].from}-${
          router.filterRanges[option.key].to
        }`,
        name,
        type: option.key,
        isGrouped: false,
      });
    }
  });

  return selectedFilters;
};

export const adjustGroupedUrlParams = (
  currentValues: string,
  newValue: string,
  keep: boolean,
  categoryTree: CampaignPageQuery['categoryTree']
) => {
  let values = currentValues.split('|');
  const index = values.indexOf(newValue);

  // Exclude the value if it is found in the current key, add it otherwise
  if (index !== -1 && !keep) {
    values.splice(index, 1);
  } else {
    values.push(newValue);
  }

  // Only unique values
  values = [...new Set(values)];

  // Check if the new value was added
  if (values.includes(newValue)) {
    const node = findTreeNode(newValue, [], categoryTree);

    // Deselect any found parents
    if (node && node.parents && node.parents.length > 0) {
      values = values.filter(value => !node.parents?.includes(value));
    }

    // Deselct any found children
    if (node && node.children && node.children.length > 0) {
      if (values && values.length > 0) {
        values = values.filter(value => {
          return value === newValue || !findTreeNode(value, [], node.children);
        });
      }
    }
  }

  return values.join('|');
};
